import { useEffect, useRef } from "react";
import { FiAlertTriangle, FiX } from "react-icons/fi";

const ConfirmModal = ({ show, title, message, confirmText = "Leave", cancelText = "Cancel", onConfirm, onCancel, variant = "warning" }) => {
  const confirmBtnRef = useRef(null);

  useEffect(() => {
    if (show) {
      // Focus the cancel button for safety (prevents accidental confirm on Enter)
      setTimeout(() => confirmBtnRef.current?.focus(), 50);

      // Close on Escape key
      const handleKey = (e) => {
        if (e.key === 'Escape') onCancel();
      };
      window.addEventListener('keydown', handleKey);
      return () => window.removeEventListener('keydown', handleKey);
    }
  }, [show, onCancel]);

  if (!show) return null;

  const colors = {
    warning: { bg: 'rgba(234, 179, 8, 0.15)', icon: '#eab308', btn: '#eab308', btnHover: '#ca8a04' },
    danger: { bg: 'rgba(239, 68, 68, 0.15)', icon: '#ef4444', btn: '#ef4444', btnHover: '#dc2626' },
  };
  const c = colors[variant] || colors.warning;

  return (
    <div className="confirm-modal-overlay" onClick={onCancel}>
      <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="confirm-modal-icon" style={{ background: c.bg }}>
          <FiAlertTriangle size={28} style={{ color: c.icon }} />
        </div>

        <h3 className="confirm-modal-title">{title || "Unsaved Changes"}</h3>
        <p className="confirm-modal-message">{message}</p>

        <div className="confirm-modal-actions">
          <button className="confirm-modal-btn cancel" onClick={onCancel}>
            {cancelText}
          </button>
          <button
            ref={confirmBtnRef}
            className="confirm-modal-btn confirm"
            style={{ background: c.btn }}
            onClick={onConfirm}
          >
            {confirmText}
          </button>
        </div>
      </div>

      <style>{`
        .confirm-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10002;
          animation: confirmFadeIn 0.15s ease;
        }

        @keyframes confirmFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes confirmSlideUp {
          from { opacity: 0; transform: translateY(10px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        .confirm-modal {
          background: #1f2937;
          border-radius: 16px;
          padding: 32px;
          width: 100%;
          max-width: 400px;
          text-align: center;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255,255,255,0.06);
          animation: confirmSlideUp 0.2s ease;
        }

        .confirm-modal-icon {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
        }

        .confirm-modal-title {
          margin: 0 0 8px;
          color: #f3f4f6;
          font-size: 18px;
          font-weight: 600;
          font-family: 'Inter', -apple-system, sans-serif;
        }

        .confirm-modal-message {
          margin: 0 0 28px;
          color: #9ca3af;
          font-size: 14px;
          line-height: 1.6;
          font-family: 'Inter', -apple-system, sans-serif;
        }

        .confirm-modal-actions {
          display: flex;
          gap: 12px;
        }

        .confirm-modal-btn {
          flex: 1;
          padding: 10px 20px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s ease;
          font-family: 'Inter', -apple-system, sans-serif;
        }

        .confirm-modal-btn.cancel {
          background: transparent;
          border: 1px solid #374151;
          color: #d1d5db;
        }

        .confirm-modal-btn.cancel:hover {
          background: rgba(255, 255, 255, 0.05);
          border-color: #4b5563;
        }

        .confirm-modal-btn.confirm {
          border: none;
          color: #fff;
        }

        .confirm-modal-btn.confirm:hover {
          opacity: 0.9;
          transform: translateY(-1px);
        }
      `}</style>
    </div>
  );
};

export default ConfirmModal;
